import { AfterViewInit, Component, computed, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, inject, OnDestroy, Output, signal, ViewChild } from '@angular/core';
import { FormField } from '@angular/forms/signals';
import { OnboardingBrandService } from '../../../onboarding/service/onboarding-brand/onboarding-brand.service';
import { RouterLink } from '@angular/router';
import { SlickOnboard, isBlockedEmailDomain } from '../../service/slick-onboard/slick-onboard.service';
import { BusinessRegistrationTypeEnum } from '../../../../common/general-types/users.dto';
import { environment } from '../../../../../environments/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { getTurnstileApi, TurnstileScriptService } from '../../service/turnstile-script/turnstile-script.service';
import { GaService } from '../../../../services/ga/ga';

export type PasswordStrengthLevel = 0 | 1 | 2 | 3 | 4;
export type PasswordStrengthLabel = 'Weak' | 'Fair' | 'Medium' | 'Strong';

@Component({
  selector: 'v2-user-details',
  imports: [FormField, RouterLink],
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class UserDetailsComponent implements AfterViewInit, OnDestroy {

  @Output() stepComplete = new EventEmitter<boolean>();
  @ViewChild('turnstileContainer') turnstileContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('honeypotInput') honeypotInput!: ElementRef<HTMLInputElement>;

  passwordVisible = false;
  BusinessRegistrationTypeEnum = BusinessRegistrationTypeEnum;

  // OTP flow state
  otpSent = signal(false);
  otpVerified = signal(false);
  isSendingOtp = signal(false);
  isVerifyingOtp = signal(false);
  otpValue = signal('');
  otpError = signal<string | null>(null);
  resendCooldown = signal(0);

  // Turnstile
  private turnstileToken = signal<string | null>(null);
  private turnstileWidgetId: string | null = null;
  private cooldownInterval: ReturnType<typeof setInterval> | null = null;

  passwordStrength = computed<{ level: PasswordStrengthLevel; label: PasswordStrengthLabel }>(() => {
    const p = this.slickOnboardService.userProfileModel().password ?? '';
    if (p.length === 0) return { level: 0, label: 'Weak' };
    const hasLower = /[a-z]/.test(p);
    const hasUpper = /[A-Z]/.test(p);
    const hasNumber = /\d/.test(p);
    const hasSpecial = /[^a-zA-Z0-9]/.test(p);
    const variety = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;
    if (p.length >= 10 && variety >= 3) return { level: 4, label: 'Strong' };
    if (p.length >= 8 && variety >= 2) return { level: 3, label: 'Strong' };
    if (p.length >= 6 && variety >= 2) return { level: 2, label: 'Medium' };
    if (p.length >= 4 || variety >= 1) return { level: 1, label: 'Fair' };
    return { level: 0, label: 'Weak' };
  });

  isBlockedDomain = computed(() => {
    const e = this.slickOnboardService.userProfileModel().email;
    if (!e || !e.includes('@')) return false;
    return isBlockedEmailDomain(e);
  });

  canSendCode = computed(() => {
    const form = this.slickOnboardService.userProfileForm;
    return (
      !form.email().invalid() &&
      !this.isBlockedDomain() &&
      !this.otpVerified() &&
      !this.isSendingOtp() &&
      this.resendCooldown() === 0
    );
  });

  canSubmit = computed(() => {
    const form = this.slickOnboardService.userProfileForm;
    return (
      !form.firstName().invalid() &&
      !form.lastName().invalid() &&
      !form.email().invalid() &&
      !form.password().invalid() &&
      !this.isBlockedDomain() &&
      this.otpVerified()
    );
  });

  private onboardingBrandService = inject(OnboardingBrandService);
  public slickOnboardService = inject(SlickOnboard);
  private turnstileScript = inject(TurnstileScriptService);
  private gaService = inject(GaService);



  ngAfterViewInit(): void {
    void this.initTurnstile();
  }

  private async initTurnstile(): Promise<void> {
    try {
      await this.turnstileScript.load();
    } catch {
      return;
    }
    const turnstile = getTurnstileApi();
    if (!turnstile || !this.turnstileContainer?.nativeElement) {
      return;
    }
    this.turnstileWidgetId = turnstile.render(this.turnstileContainer.nativeElement, {
      sitekey: environment.cloudflare.turnstileSiteKey,
      callback: (token: string) => this.turnstileToken.set(token),
      'expired-callback': () => this.turnstileToken.set(null),
      'error-callback': () => this.turnstileToken.set(null),
      theme: 'light',
      size: 'normal',
    });
  }

  ngOnDestroy(): void {
    if (this.cooldownInterval) clearInterval(this.cooldownInterval);
    if (this.turnstileWidgetId) {
      getTurnstileApi()?.remove(this.turnstileWidgetId);
      this.turnstileWidgetId = null;
    }
  }

  async sendCode(): Promise<void> {
    if (!this.canSendCode()) return;

    // Honeypot check — silently do nothing if filled by a bot
    if (this.honeypotInput?.nativeElement?.value) return;

    this.isSendingOtp.set(true);
    this.otpError.set(null);
    try {
      await this.slickOnboardService.sendEmailOtp(this.turnstileToken(), this.honeypotInput?.nativeElement?.value ?? '');
      this.otpSent.set(true);
      this.gaService.trackOnboardingEvent('user_details_email_verification_sent');
      this.startResendCooldown();
    } catch (err) {
      const message = err instanceof HttpErrorResponse ? err.error?.message ?? 'Failed to send code' : 'Failed to send code';
      this.otpError.set(Array.isArray(message) ? message[0] : message);
      if (this.turnstileWidgetId) getTurnstileApi()?.reset(this.turnstileWidgetId);
    } finally {
      this.isSendingOtp.set(false);
    }
  }

  async verifyCode(): Promise<void> {
    const otp = this.otpValue().trim();
    if (otp.length !== 6 || this.isVerifyingOtp()) return;

    this.isVerifyingOtp.set(true);
    this.otpError.set(null);
    try {
      await this.slickOnboardService.verifyEmailOtp(otp);
      this.otpVerified.set(true);
    } catch (err) {
      const message = err instanceof HttpErrorResponse ? err.error?.message ?? 'Invalid code' : 'Invalid code';
      this.otpError.set(Array.isArray(message) ? message[0] : message);
    } finally {
      this.isVerifyingOtp.set(false);
    }
  }

  onOtpInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 6);
    input.value = digits;
    this.otpValue.set(digits);
    this.otpError.set(null);
    if (digits.length === 6) this.verifyCode();
  }

  private startResendCooldown(): void {
    this.resendCooldown.set(60);
    this.cooldownInterval = setInterval(() => {
      const current = this.resendCooldown();
      if (current <= 1) {
        this.resendCooldown.set(0);
        if (this.cooldownInterval) clearInterval(this.cooldownInterval);
      } else {
        this.resendCooldown.set(current - 1);
      }
    }, 1000);
  }

  async saveProfileInfo(): Promise<void> {
    if (!this.canSubmit()) return;

    this.onboardingBrandService.userProfileInformation = {
      firstName: this.slickOnboardService.userProfileModel().firstName,
      lastName: this.slickOnboardService.userProfileModel().lastName,
      email: this.slickOnboardService.userProfileModel().email,
      password: this.slickOnboardService.userProfileModel().password,
    };

    this.gaService.trackOnboardingEvent('user_details_profile_submitted');
    this.stepComplete.emit(true);
  }
}
